import removeMd from 'remove-markdown';
import { Link } from 'stac-js';
import { hasText, isObject, size, URI } from 'stac-js/src/utils.js';
import { pagination } from "stac-js/src/relationtypes.js";

export const commonFileNames = ['catalog', 'collection', 'item'];

export class BrowserError extends Error {
  constructor(message) {
    super(message);
  }
}

/**
 * General utilities
 * 
 * @class
 */
export default class Utils {

  static shortenTitle(fullStr, strLen, separator = '…') {
    if (fullStr.length <= strLen) {
      return fullStr;
    }

    let sepLen = separator.length;
    let charsToShow = strLen - sepLen;
    let frontChars = Math.ceil(charsToShow/2);
    let backChars = Math.floor(charsToShow/2);
    return fullStr.substr(0, frontChars) + 
           separator + 
           fullStr.substr(fullStr.length - backChars);
  }

  static getLinkWithRel(links, rel) {
    return Array.isArray(links) ? links.find(link => isObject(link) && hasText(link.href) && link.rel === rel) : null;
  }

  static getLinksWithRels(links, rels) {
    return Array.isArray(links) ? links.filter(link => isObject(link) && hasText(link.href) && rels.includes(link.rel)) : [];
  }

  static removeTrailingSlash(str) {
    return str.replace(/\/+$/, '');
  }

  static equalUrl(a, b) {
    try {
      let uri1 = URI(a);
      let uri2 = URI(b);
      // Ignore trailing slash in URL paths
      uri1.path(Utils.removeTrailingSlash(uri1.path()));
      uri2.path(Utils.removeTrailingSlash(uri2.path()));
      return uri1.equals(uri2);
    } catch {
      return false;
    }
  }

  /**
   * Checks whether two URLs point to the same resource, ignoring a trailing
   * slash in the path. Query parameters and fragments are ignored.
   *
   * @param {string|URI} a - The first URL
   * @param {string|URI} b - The second URL
   * @returns {boolean} `true` if scheme, authority and path (except for a trailing slash) are equal
   */
  static samePath(a, b) {
    if (!a || !b) {
      return false;
    }
    try {
      const uri1 = URI(a);
      const uri2 = URI(b);
      return uri1.scheme().toLowerCase() === uri2.scheme().toLowerCase()
        && uri1.authority().toLowerCase() === uri2.authority().toLowerCase()
        && Utils.removeTrailingSlash(uri1.path()) === Utils.removeTrailingSlash(uri2.path());
    } catch {
      return false;
    }
  }

  /**
   * Returns the given URL with the trailing slash of the path added or removed.
   * Scheme, authority, query and fragment are preserved.
   *
   * @param {string} url - The URL to change
   * @param {boolean} trailingSlash - `true` to add a trailing slash, `false` to remove it
   * @returns {string} The updated URL
   */
  static setTrailingSlash(url, trailingSlash) {
    try {
      const uri = URI(url);
      let path = Utils.removeTrailingSlash(uri.path());
      if (trailingSlash) {
        path += '/';
      }
      return uri.path(path).toString();
    } catch {
      return url;
    }
  }

  static summarizeMd(text, maxLength = null) {
    if (!hasText(text)) {
      return '';
    }
    // Best-effort approach to remove some CommonMark (Markdown).
    // Likely not perfect, but seems good enough for most cases.
    text = removeMd(text).replaceAll(/[\r\n]+/g, ' ');
    if (maxLength > 0 && text.length > maxLength) {
      text = text.substr(0, maxLength) + '…';
    }
    return text;
  }

  static scrollTo(el) {
    if (!el) {
      return;
    }
    let rect = el.getBoundingClientRect();
    let isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
    if (!isVisible) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }

  // Convert from UTC to locale time (needed for vue2-datetimepicker)
  // see https://github.com/mengxiong10/vue2-datepicker/issues/388
  static dateFromUTC(dt) {
    if (dt) {
      const value = new Date(dt);
      dt = new Date(value.getTime() + value.getTimezoneOffset() * 60 * 1000);
    }
    return dt;
  }

  static dateToUTC(dt) {
    if (dt instanceof Date) {
      dt = new Date(dt.getTime() - dt.getTimezoneOffset() * 60 * 1000);
    }
    return dt;
  }

  static formatDatetimeQuery(value) {
    if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
      return value.map(dt => {
        if (dt instanceof Date) {
          return dt.toISOString();
        }
        else {
          return dt || '..';
        }
      }).join('/');
    }
    return null;
  }

  static formatSortbyForPOST(value) {
    // POST search requires sortby to be an array of objects containing a property name and sort direction.
    // See spec here: https://api.stacspec.org/v1.0.0-rc.1/item-search/#tag/Item-Search/operation/postItemSearch
    // This function converts the property name to the desired format.
    const sortby = {
      field: '',
      direction: 'asc'
    };
  
    // Check if the value starts with a minus sign ("-")
    if (value.startsWith('-')) {
      // sort by descending order
      sortby.field = value.substring(1);
      sortby.direction = 'desc';
    } else {
      //sort by ascending order
      sortby.field = value;
    }
    
    // Put the object in an array
    return [sortby];
  }

  static stateQueryParametersToObject(state, query = {}) {
    for (const [key, value] of Object.entries(state)) {
      let name = `.${key}`;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          query[name] = value.join(',');
        }
      }
      else if (value !== null) {
        query[name] = value;
      }
    }
    return query;
  }

  // todo: remove when all usage is gone, replace with stac-js method
  static getPaginationLinks(data) {
    let pages = {};
    if (isObject(data)) {
      let pageLinks = Utils.getLinksWithRels(data.links, pagination);
      for (let pageLink of pageLinks) {
        let rel = pageLink.rel === 'previous' ? 'prev' : pageLink.rel;
        pages[rel] = pageLink;
      }
    }
    return pages;
  }

  static addFiltersToLink(link, filters = {}, defaultLimit = null, defaultSort = null) {
    let isEmpty = value => {
      return (value === null
      || (typeof value === 'number' && !Number.isFinite(value))
      || (typeof value === 'string' && value.length === 0)
      || (typeof value === 'object' && size(value) === 0));
    };

    if (!isObject(filters)) {
      filters = {};
    }
    else {
      filters = Object.assign({}, filters);
    }

    if (typeof filters.limit !== 'number' && typeof defaultLimit === 'number') {
      filters.limit = defaultLimit;
    }
    if (typeof filters.sortby !== 'string' && typeof defaultSort === 'string') {
      filters.sortby = defaultSort;
    }

    if (hasText(link.method) && link.method.toUpperCase() === 'POST') {
      let body = Object.assign({}, link.body);

      for (let key in filters) {
        let value = filters[key];
        if (isEmpty(value)) {
          delete body[key];
          continue;
        }

        if (key === 'sortby') {
          value = Utils.formatSortbyForPOST(value);
        }
        else if (key === 'datetime') {
          value = Utils.formatDatetimeQuery(value);
          if (!value) {
            continue; // skip empty datetime
          }
        }
        else if (key === 'filters') {
          if (typeof value.serialize === 'function') {
            Object.assign(body, value.serialize(link.method));
          }
          continue;
        }

        body[key] = value;
      }
      const newLink = new Link(link);
      newLink.body = body;
      return newLink;
    }
    else { // GET
      // Construct new link with search params
      const url = URI(link.href);

      for (let key in filters) {
        let value = filters[key];
        if (isEmpty(value)) {
          url.removeQuery(key);
          continue;
        }

        if (key === 'datetime') {
          value = Utils.formatDatetimeQuery(value);
        }
        else if (key === 'bbox') {
          value = value.join(',');
        }
        else if ((key === 'collections' || key === 'ids' || key === 'q')) {
          value = value.join(',');
        }
        else if (key === 'filters') {
          if (typeof value.serialize === 'function') {
            let params = value.serialize('GET');
            // JSON filter objects must be stringified for query params
            if (typeof params.filter === 'object') {
              params = { ...params, filter: JSON.stringify(params.filter) };
            }
            url.setQuery(params);
          }
          continue;
        }

        url.setQuery(key, value);
      }

      const newLink = new Link(link);
      newLink.href = url.toString();
      return newLink;
    }
  }

  static getIcon(data) {
    if (data?.isSTAC) {
      const icons = data.getIcons();
      if (icons.length > 0) {
        return icons[0];
      }
    }
    return null;
  }

  static titleForHref(href, preferFileName = false) {
    const uri = URI(href);
    const auth = uri.authority();
    const file = uri.filename().replace(/^(.{1,})\.\w+$/, '$1');
    if (auth && file && !preferFileName) {
      const path = uri.path().replace(/^\//, '');
      if (auth === 'doi.org' && path.startsWith('10.')) {
        return `DOI ${path}`;
      }
      return `${file} (${auth})`;
    }
    if (file && !commonFileNames.includes(file)) {
      return file;
    }
    const dir = uri.segmentCoded(-2);
    return dir || auth || href;
  }

  static search(searchterm, target, and = true) {
    if (typeof searchterm !== 'string' || searchterm.length === 0) {
      return false;
    }
    if (isObject(target)) {
      target = Object.values(target);
    }
    else if (typeof target === 'string') {
      target = [target];
    }

    if (!Array.isArray(target)) {
      return false;
    }

    let splitChars = /[\s.,;!&({[)}]]+/g;

    // Prepare search terms
    searchterm = searchterm.toLowerCase().split(splitChars);

    // Prepare text to search in
    target = target
      .filter(s => typeof s === 'string') // Remove non-strings
      .join(' ') // Merge into a single string
      .replace(splitChars, ' ') // replace split chars with white spaces
      .toLowerCase(); // Lowercase

    // Search with "and" or "or"
    let fn = and ? 'every' : 'some';
    return searchterm[fn](term => target.includes(term));
  }

  static createLink(href, rel, title) {
    return new Link({ href, rel, title });
  }

  /**
   * Deep merge two objects.
   * @param target
   * @param ...sources
   */
  static mergeDeep(target, ...sources) {
    if (!sources.length) {
      return target;
    }
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
      for (const key in source) {
        if (isObject(source[key])) {
          if (!target[key]) {
            Object.assign(target, { [key]: {} });
          }
          Utils.mergeDeep(target[key], source[key]);
        } else {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }

    return Utils.mergeDeep(target, ...sources);
  }

  static parseApiSortParameter(value) {
    if (typeof value !== 'string') {
      return { field: null, direction: 0 };
    }
    if (value.startsWith('-')) {
      return { field: value.substring(1), direction: -1 };
    }
    else {
      if (value.startsWith('+')) {
        value = value.substring(1);
      }
      return { field: value, direction: 1 };
    }
  }

  static assetFilename(asset, response = null) {
    // Get the preferred filename from the file:local_path property
    if (asset?.isAsset) {
      const localPath = asset.getMetadata('file:local_path');
      if (typeof localPath === 'string') {
        return URI(localPath).filename();
      }
    }
    // Get the filename from the content-disposition header
    const contentDisposition = response?.headers.get('content-disposition');
    if (typeof contentDisposition === 'string') {
      const parts = contentDisposition.match(/filename=(?:"|)([^"]+)(?:"|)(?:;|$)/);
      if (parts) {
        return parts[1];
      }
    }
    // Fallback to the filename from the href
    if (isObject(asset) && typeof asset.href === 'string') {
      return URI(asset.href).filename();
    }
    // Fallback to a default filename
    return 'download';
  }

}
